import { useState, useEffect } from "react";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";

export default function MeinKonto() {
  const { user: authUser, authMode } = useAuth();
  const [user, setUser] = useState(null);
  const [schritt, setSchritt] = useState("uebersicht"); // uebersicht | einrichten | code-eingeben | backup-codes
  const [setupDaten, setSetupDaten] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [passwort, setPasswort] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Vorher wurde NUR localStorage("internal_user") gelesen - das blieb
    // für Keycloak/SSO-Logins immer leer (bewusst, siehe AuthContext.jsx),
    // wodurch diese Seite komplett leer blieb. Der gemeinsame Auth-Kontext
    // (useAuth) funktioniert für beide Modi.
    if (authUser) {
      setUser({ ...authUser, mfa_aktiv: authUser.mfa_aktiv ?? false });
      return;
    }
    const u = localStorage.getItem("internal_user");
    if (u) setUser(JSON.parse(u));
  }, [authUser]);

  const mfaEinrichtenStarten = async () => {
    setLoading(true); setError("");
    try {
      const d = await api.mfaEinrichten();
      setSetupDaten(d);
      setSchritt("einrichten");
    } catch { setError("Fehler beim Einrichten"); }
    finally { setLoading(false); }
  };

  const codeBestaetigen = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const d = await api.mfaAktivieren(code);
      setBackupCodes(d.backup_codes);
      setSchritt("backup-codes");
      const u = { ...user, mfa_aktiv: true };
      setUser(u);
      localStorage.setItem("internal_user", JSON.stringify(u));
    } catch {
      setError("Ungültiger Code - bitte erneut versuchen");
    } finally { setLoading(false); }
  };

  const mfaDeaktivieren = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.mfaDeaktivieren(passwort);
      const u = { ...user, mfa_aktiv: false };
      setUser(u);
      localStorage.setItem("internal_user", JSON.stringify(u));
      setSchritt("uebersicht");
      setPasswort("");
    } catch {
      setError("Falsches Passwort");
    } finally { setLoading(false); }
  };

  if (!user) return null;

  const eigeneDatenExportieren = async () => {
    const daten = await api.exportEigeneDaten();
    const blob = new Blob([JSON.stringify(daten, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "meine_daten.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 24, maxWidth: 480 }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Mein Konto</h1>
      <div style={{ fontSize: 14, color: "#666", marginBottom: 24 }}>{user.email}</div>

      {authMode === "internal" && (
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Meine Daten</h2>
          <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
            Lade eine Kopie aller bei FoxFlow über dich gespeicherten Daten herunter.
          </div>
          <button className="btn btn-secondary" onClick={eigeneDatenExportieren}>
            ⬇ Meine Daten herunterladen
          </button>
        </div>
      )}

      {authMode !== "internal" ? (
        <div className="card" style={{ padding: 20 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Zwei-Faktor-Authentifizierung (2FA)</h2>
          <div style={{ fontSize: 13, color: "#666" }}>
            Du bist über Single Sign-On (SSO) angemeldet. Die 2FA-Einrichtung auf dieser Seite gilt
            nur für Konten mit internem Passwort-Login - deine Zwei-Faktor-Authentifizierung wird
            bei deinem SSO-Anbieter (z.B. Keycloak) verwaltet, nicht hier.
          </div>
        </div>
      ) : (
      <div className="card" style={{ padding: 20 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Zwei-Faktor-Authentifizierung (2FA)</h2>

        {error && <div style={{ background: "var(--danger-bg)", color: "var(--danger)", padding: "10px 14px", borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{error}</div>}

        {schritt === "uebersicht" && (
          user.mfa_aktiv ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, color: "var(--ok)" }}>
                <span style={{ fontSize: 20 }}>✓</span>
                <span>2FA ist aktiv</span>
              </div>
              <form onSubmit={mfaDeaktivieren}>
                <div style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>
                  Zum Deaktivieren bitte dein aktuelles Passwort eingeben:
                </div>
                <input type="password" className="form-control" value={passwort}
                       onChange={e => setPasswort(e.target.value)} required
                       style={{ marginBottom: 12 }} placeholder="Aktuelles Passwort" />
                <button type="submit" className="btn btn-secondary" disabled={loading}>
                  {loading ? "…" : "2FA deaktivieren"}
                </button>
              </form>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
                Schützt dein Konto zusätzlich mit einer Authenticator-App (z.B. Google Authenticator, Authy).
              </div>
              <button className="btn btn-primary" onClick={mfaEinrichtenStarten} disabled={loading}>
                {loading ? "…" : "2FA einrichten"}
              </button>
            </>
          )
        )}

        {schritt === "einrichten" && setupDaten && (
          <>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
              1. QR-Code mit deiner Authenticator-App scannen (oder das Secret manuell eintragen):
            </div>
            <div style={{ textAlign: "center", marginBottom: 12 }}>
              <img src={`data:image/png;base64,${setupDaten.qr_base64}`} alt="QR-Code" style={{ width: 200, height: 200 }} />
            </div>
            <div style={{ fontSize: 12, color: "#999", textAlign: "center", marginBottom: 20, fontFamily: "monospace", wordBreak: "break-all" }}>
              {setupDaten.secret}
            </div>
            <form onSubmit={codeBestaetigen}>
              <div style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>
                2. Den 6-stelligen Code aus der App eingeben:
              </div>
              <input type="text" inputMode="numeric" className="form-control" value={code}
                     onChange={e => setCode(e.target.value)} required autoFocus
                     placeholder="123456" style={{ marginBottom: 12, letterSpacing: 2, fontSize: 18, textAlign: "center" }} />
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "…" : "Bestätigen & aktivieren"}
              </button>
              <button type="button" className="btn btn-ghost" style={{ marginLeft: 8 }}
                      onClick={() => { setSchritt("uebersicht"); setCode(""); setError(""); }}>
                Abbrechen
              </button>
            </form>
          </>
        )}

        {schritt === "backup-codes" && backupCodes && (
          <>
            <div style={{ background: "#fff8e1", padding: "12px 14px", borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
              <strong>Wichtig:</strong> Speichere diese Backup-Codes an einem sicheren Ort. Jeder Code
              funktioniert einmalig als Ersatz für die Authenticator-App, falls du dein Gerät verlierst.
              Sie werden dir nur jetzt einmalig angezeigt.
            </div>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
              fontFamily: "monospace", fontSize: 14, background: "#f5f5f5",
              padding: 16, borderRadius: 6, marginBottom: 20
            }}>
              {backupCodes.map((c, i) => <div key={i}>{c}</div>)}
            </div>
            <button className="btn btn-primary" onClick={() => setSchritt("uebersicht")}>
              Codes gespeichert, fertig
            </button>
          </>
        )}
      </div>
      )}
    </div>
  );
}
